import { useLingui } from '@lingui/react/macro';
import type { FC } from 'react';
import type { WealthCoin } from '@/backend';
import { useCoin } from './api';
import { cn } from './react';

export type CoinProps = {
  className?: string;
  coin?: string | WealthCoin;
  size: 10 | 8 | 6 | 5;
  quote?: string | WealthCoin;
  block?: boolean;
  variant?: 'full' | 'name' | 'symbol_icon' | 'symbol' | 'icon';
  /** Set for above-the-fold coins to improve LCP (eager load + high fetch priority). */
  priority?: boolean;
};

const imgCommon = {
  decoding: 'async' as const,
};

export const Coin: FC<CoinProps> = ({
  className,
  block,
  size,
  coin,
  quote,
  variant,
  priority = false,
}) => {
  const baseObject = useCoin(coin);
  const quoteObject = useCoin(quote);
  const { i18n } = useLingui();
  const name =
    (i18n.locale !== 'en'
      ? baseObject.data?.iraqi_name
      : baseObject.data?.name) || (typeof coin === 'string' ? coin : '');
  const symbol =
    baseObject.data?.symbol || (typeof coin === 'string' ? coin : '');

  return (
    <div
      className={cn(
        block ? 'flex' : 'inline-flex',
        'gap-1 items-center overflow-hidden truncate',
        className,
      )}
    >
      {variant === 'name' ? (
        name
      ) : variant === 'symbol' ? (
        symbol
      ) : variant === 'symbol_icon' ? (
        <>
          <img
            src={baseObject.data?.image}
            width={24}
            height={24}
            className={cn(
              size === 5 && 'size-5',
              size === 6 && 'size-6',
              size === 8 && 'size-8',
              size === 10 && 'size-10',
              'shrink-0 rounded-full overflow-hidden border border-border-disabled dark:bg-background-inverse',
            )}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
            {...imgCommon}
            alt={name || symbol || ''}
          />
          <p className="flex gap-1 items-center truncate">
            <span>{symbol}</span>
          </p>
        </>
      ) : variant === 'icon' ? (
        <img
          src={baseObject.data?.image}
          width={24}
          height={24}
          className={cn(
            size === 5 && 'size-5',
            size === 6 && 'size-6',
            size === 8 && 'size-8',
            size === 10 && 'size-10',
            'shrink-0 rounded-full overflow-hidden border border-border-disabled dark:bg-background-inverse',
          )}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : undefined}
          {...imgCommon}
          alt={name || symbol || ''}
        />
      ) : (
        <>
          <img
            src={baseObject.data?.image}
            width={24}
            height={24}
            className={cn(
              size === 5 && 'size-5',
              size === 6 && 'size-6',
              size === 8 && 'size-8',
              size === 10 && 'size-10',
              'shrink-0 rounded-full overflow-hidden border border-border-disabled dark:bg-background-inverse',
            )}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
            {...imgCommon}
            alt={name || symbol || ''}
          />
          <p className="flex gap-1 items-center truncate">
            {i18n.locale !== 'en' ? (
              <>
                <span>{name}</span>
                <abbr className="text-content-tertiary text-[85%]">
                  ({baseObject.data?.symbol})
                </abbr>
              </>
            ) : (
              <>
                <abbr>{symbol}</abbr>
                <span className="text-content-tertiary text-[85%]">
                  ({name})
                </span>
              </>
            )}
          </p>
        </>
      )}
      {quote && (
        <span className="opacity-70">/ {quoteObject.data?.symbol || ''}</span>
      )}
    </div>
  );
};
